"use client";

import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { benefits } from "./data";
import { useComponentTranslations } from "@/app/components/LanguageProvider";
import CenterImage from "./CenterImage";
import BenefitCard from "./BenefitCard";
import ConnectionLines from "./ConnectionLines";

function lerp(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  const t = Math.max(0, Math.min(1, (value - inMin) / (inMax - inMin)));
  return outMin + t * (outMax - outMin);
}

const CARD_BASE = 0.22;
const CARD_STEP = 0.09;
const CARD_DUR = 0.08;
const IMG_IN: [number, number] = [0.08, 0.2];
const LINE_IN: [number, number] = [0.08, 0.3];

export default function WhyDev48Desktop() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [cardPositions, setCardPositions] = useState<{ x: number; y: number }[]>([]);
  const [gridSize, setGridSize] = useState({ w: 0, h: 0 });
  const { t } = useComponentTranslations("WhyDev48");

  const [scrollValue, setScrollValue] = useState(0);

  // Scroll progress of the section — equivalent to useScroll({target, offset: ["start start", "end end"]})
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const progress = total > 0 ? -rect.top / total : 0;
      setScrollValue(Math.max(0, Math.min(1, progress)));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const imageStyle = useMemo(() => {
    const p = lerp(scrollValue, ...IMG_IN, 0, 1);
    return {
      opacity: p,
      scale: lerp(p, 0, 1, 0.6, 1),
      filter: `blur(${lerp(p, 0, 1, 30, 0)}px)`,
    };
  }, [scrollValue]);

  const cardStyles = useMemo(() => {
    return benefits.map((_, index) => {
      const start = CARD_BASE + index * CARD_STEP;
      const p = lerp(scrollValue, start, start + CARD_DUR, 0, 1);
      return {
        opacity: p,
        y: lerp(p, 0, 1, 30, 0),
        scale: lerp(p, 0, 1, 0.95, 1),
        filter: `blur(${lerp(p, 0, 1, 10, 0)}px)`,
      };
    });
  }, [scrollValue]);

  const lineProgress = useMemo(() => {
    return lerp(scrollValue, ...LINE_IN, 0, 1);
  }, [scrollValue]);

  const measureCards = useCallback(() => {
    if (!gridRef.current) return;
    const grid = gridRef.current;
    const gridRect = grid.getBoundingClientRect();
    const positions: { x: number; y: number }[] = [];

    cardRefs.current.forEach((el) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      positions.push({
        x: rect.left + rect.width / 2 - gridRect.left,
        y: rect.top + rect.height / 2 - gridRect.top,
      });
    });

    if (positions.length === 6) {
      setCardPositions(positions);
      setGridSize({ w: gridRect.width, h: gridRect.height });
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(measureCards, 500);
    window.addEventListener("resize", measureCards);

    if (gridRef.current) {
      const ro = new ResizeObserver(() => {
        setTimeout(measureCards, 100);
      });
      ro.observe(gridRef.current);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", measureCards);
        ro.disconnect();
      };
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measureCards);
    };
  }, [measureCards]);

  const setCardRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      cardRefs.current[index] = el;
    },
    []
  );

  const translatedBenefits = useMemo(
    () =>
      benefits.map((b) => ({
        ...b,
        title: t(b.titleKey as never) ?? b.titleKey,
        text: t(b.descKey as never) ?? b.descKey,
      })),
    [t]
  );

  const leftItems = useMemo(() => translatedBenefits.filter((x) => x.side === "left"), [translatedBenefits]);
  const rightItems = useMemo(() => translatedBenefits.filter((x) => x.side === "right"), [translatedBenefits]);

  return (
    <section ref={sectionRef} className="relative h-[430vh]">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 lg:px-6 relative z-10">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold tracking-tight text-white/95">
              {t("sectionTitle" as never)}
            </h2>
            <p className="mt-3 text-sm text-white/70 max-w-2xl mx-auto">
              {t("sectionSubtitle" as never)}
            </p>
          </div>
          <div ref={gridRef} className="relative grid grid-cols-[1fr_360px_1fr] md:gap-0 lg:gap-6 items-center overflow-visible">
            <ConnectionLines progressValue={lineProgress} cardPositions={cardPositions} gridSize={gridSize} />
            <div className="space-y-4 z-10 md:-mr-16 lg:mr-0">
              {leftItems.map((item, index) => (
                <div key={item.id} ref={setCardRef(index)}>
                  <BenefitCard item={item} scrollStyle={cardStyles[index]} />
                </div>
              ))}
            </div>
            <div className="relative z-0">
              <CenterImage scrollStyle={imageStyle} />
            </div>
            <div className="space-y-4 z-10 md:-ml-16 lg:ml-0">
              {rightItems.map((item, index) => (
                <div key={item.id} ref={setCardRef(index + 3)}>
                  <BenefitCard item={item} scrollStyle={cardStyles[index + 3]} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
